import React from 'react';
import { LoadingOverlay } from '../../../../components';
import { fetchSpecialistByIdV2, fetchSpecialistsV2, fetchUnitsFilter } from '../../../../modules/specialists';
import { toast } from 'react-toastify';
import { Specialist, UnitFilter } from '../../../../models/Specialist';
import { UnitsTemplate } from '../../../../templates/UnitsTemplate';
import useOnCloseSchedule from '../../../../hooks/useOnCloseSchedule';
import { Page, useCareplusRouter } from '../../router/context';
import { useUnits } from '../../store/Units';
import { envConfig } from '../../../../utils/enviroment';
import { useSchedule } from '../../store/Schedule';
import { useSpecialist } from '../../store/Specialist';
import { ModalSystemUnavailable } from '../../components/ModalSystemUnavailable';
import { usePayment } from '../../hooks/usePayment';
import { usePatientContext } from '../../../../contexts/PatientContext';
import { useSpecialties } from '../../store/Specialties';
import { sendToWaitingList } from '../../../../modules/appointmentBook';

export function UnitList(state: any) {
  const [loading, setLoading] = React.useState(false);
  const [units, setUnits] = React.useState<UnitFilter[]>([]);
  const [selectedUnit, setSelectedUnit] = React.useState<UnitFilter>();
  const [nextPage, setNextPage] = React.useState(Page.SPECIALIST_LIST);
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);

  const { onCloseSchedule } = useOnCloseSchedule({});
  const { navigate, goBack } = useCareplusRouter();
  const { setUnit, scheduleBuild, setResource, setPayment } = useSchedule();
  const { setSelectedUnit: setSelectedUnitStore } = useUnits();
  const { setSpecialistSelected, specialistSelected, linkedSpecialist, setSpecialists } = useSpecialist();
  const { patientSelected } = usePatientContext();
  const { specialtySelected } = useSpecialties();

  const { getPayment } = usePayment({ handleGenericErrors: () => setIsModalSystemUnavailableVisible(e => !e) });

  const convertUnitsName = (units: UnitFilter[]) => {
    return units.map(item => {
      const itemLowerCase = item.unit_name.toLowerCase();
      item.unit_name = itemLowerCase.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
      return item;
    });
  };

  const getUnitsDoctor = (specialist: Specialist) => {
    let unitsFilter = [] as UnitFilter[];

    unitsFilter = specialist.units.map(
      un =>
        ({
          unit_name: un.name,
          unit_address: un.full_address,
          unit_id: un.id,
          business_unit_type: un.business_unit_type
        } as UnitFilter)
    );

    setUnits(unitsFilter);
  };

  const getSpecialistById = async (specialistId: string) => {
    setLoading(r => !r);

    const result = await fetchSpecialistByIdV2(specialistId);

    if (result.error) toast.error('Não foi possível buscar dados do especialista');

    setLoading(r => !r);

    if (!result.error) {
      const specialist = result.data;

      getUnitsDoctor(specialist);
      setNextPage(Page.APPOINTMENT_TYPE);
      setSpecialistSelected(specialist);

      return result.data;
    }
  };

  const getUnits = async () => {
    setLoading(r => !r);

    const response = await fetchUnitsFilter(false);
    if (response.error) toast.error('Não foi possível consultar unidades');

    if (!response.error) setUnits(convertUnitsName(response.data));

    setLoading(r => !r);
  };

  React.useEffect(() => {
    if (linkedSpecialist) getSpecialistById(String(linkedSpecialist));
    else getUnits();
  }, []);

  const onGoBack = () => {
    if (state?.IS_FIRST_APPOINTMENT) return onCloseSchedule();
    return goBack();
  };

  const unitsOrdered = React.useMemo(() => {
    return units.sort((unit, unit2) => {
      const item1 =
        unit?.business_unit_type?.name.toUpperCase() === envConfig.REACT_APP_CARE_PLUS_TYPE_GROUP_UNITS ? -1 : 1;
      const item2 =
        unit2?.business_unit_type?.name.toUpperCase() === envConfig.REACT_APP_CARE_PLUS_TYPE_GROUP_UNITS ? -1 : 1;

      return item1 - item2 || unit.unit_name.localeCompare(unit2.unit_name);
    });
  }, [units]);

  const checkUnitHasDoctors = async (unitId: string) => {
    const response = await fetchSpecialistsV2({
      specialty: specialtySelected?.id,
      unit: unitId,
      show_unavailable_on_digital_platform: 1
    });

    if (response?.data?.length > 0) {
      setSpecialists(response.data || []);

      return true;
    }

    return false;
  };

  const handleExternalUnit = async (unit: UnitFilter) => {
    await sendToWaitingList({
      fullName: patientSelected?.name ?? '',
      email: patientSelected?.email ?? '',
      phone: patientSelected?.phone ?? '',
      catalogUnitId: unit.unit_id,
      catalogUnitName: unit.unit_name,
      catalogSpecialityId: specialtySelected?.id ?? '',
      catalogSpecialtyDescription: specialtySelected?.name ?? '',
      isDeleted: false,
      deletedBy: null,
      document: patientSelected?.document ?? '',
      documentType: patientSelected?.documentType ?? ''
    });
    navigate(Page.PENDING_SCHEDULE_PAGE);
  };

  const bindPaymentToSchedule = async (unitId: string) => {
    const payment = await getPayment({
      officeId: unitId,
      specialistId: String(specialistSelected?.user_id),
      subgroup: patientSelected?.eligibilitySubGroup || ''
    });

    if (payment) setPayment(payment);
  };

  const handleSelectedUnit = async (unit?: UnitFilter) => {
    if (specialistSelected.user_id && unit) {
      await bindPaymentToSchedule(unit.unit_id);

      setResource({
        resourceExternalId: specialistSelected.id,
        resourceId: specialistSelected.user_id,
        name: specialistSelected.name,
        specialtyId: specialtySelected?.id ?? ''
      });
    }

    setUnit({ externalUnitId: unit?.unit_id || '' });
    setSelectedUnit(unit);
    scheduleBuild();
  };

  const deactivateUnit = (unitId: string) => {
    setUnits(units =>
      units.map(unit => {
        if (unit.unit_id === unitId) unit.active = false;
        return unit;
      })
    );
  };

  const submit = async () => {
    setLoading(r => !r);

    if (
      selectedUnit?.business_unit_type?.name.toUpperCase() !== String(envConfig.REACT_APP_CARE_PLUS_TYPE_GROUP_UNITS)
    ) {
      if (selectedUnit) await handleExternalUnit(selectedUnit);
    } else {
      const unitHasDoctors = await checkUnitHasDoctors(selectedUnit.unit_id);

      if (unitHasDoctors) {
        setSelectedUnitStore(selectedUnit?.unit_id);
        navigate(nextPage, { ...state });
      } else {
        setIsModalSystemUnavailableVisible(true);

        deactivateUnit(selectedUnit.unit_id);
        handleSelectedUnit();
      }

      setLoading(r => !r);
    }
  };

  return (
    <>
      <UnitsTemplate
        units={unitsOrdered}
        onSubmit={submit}
        goBack={onGoBack}
        handleSelectedUnit={handleSelectedUnit}
        disabledSubmitButton={!selectedUnit}
        selectedUnit={selectedUnit?.unit_id || ''}
      />

      <ModalSystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
        callToSupport={() => (location.href = 'https://wa.me/11964079032?whatsapp=11964079032')}
        goToHome={onCloseSchedule}
      />
      <LoadingOverlay loading={loading} />
    </>
  );
}
